import { useEffect, useMemo, useRef, useState, type CSSProperties, type JSX } from 'react';
import { useTurnProgress } from '../../components/Countdown.js';
import { Icon } from '../../components/Icon.js';
import { useLang, useT, type CopyKey, type T } from '../../i18n/index.js';
import { socket } from '../../net/socket.js';
import { useApp } from '../../state/store.js';
import { GameRoom } from '../parts/GameRoom.js';
import { TileFace, useTileGlyphs, type TileSize } from './tileArt.js';
import './MahjongBoard.css';

/**
 * S6 — Mahjong under the karport (design 2a / 3a). Presentation only.
 *
 * Every affordance is read off `view.legal`, which the engine publishes for the
 * seat on the clock alone (packages/engines/src/mahjong). Nothing here decides a
 * rule, and mahjong has several that no client should be trusted with:
 *
 *  - a chow is only legal from the seat to your left, and one tile can make up
 *    to three different runs — the engine sends the exact three tiles of each;
 *  - a claim window is a QUEUE, polled one seat at a time by priority, so
 *    "somebody could call this" is a turn like any other and has a real clock;
 *  - a kang comes in three flavours and only one of them is claimed off a
 *    discard; the other two are declared out of your own rack after a draw.
 *
 * A board that guessed at any of those would offer taps the server rejects, and
 * a rejected tap inside a 25-second turn (PRD §8) costs chips.
 *
 * STAGING. The board is the table and nothing else: GameRoom owns the station
 * bar, the camera, the pods, the pot, the toast and the action pills, so what
 * this file describes is one green felt surface, once, for both cameras.
 *
 *  - the discard pool lies FLAT in the middle of the felt, because a river is
 *    thrown down on the table and reads the same from either angle;
 *  - the wall standing round the far and side edges carries `up`, and so does
 *    each opponent's count — a rack of tiles genuinely stands, facing away from
 *    you, and standing them is what makes the 2.5D camera read as a room;
 *  - exposed melds lie flat in front of the seat that laid them, which is where
 *    they are on a real table;
 *  - your own sixteen tiles are on the near rail in screen space, the one thing
 *    you hold rather than something the table carries.
 *
 * Everything is positioned in table pixels inside `.room__felt` (the karport
 * slab is 470×330 in room.css) and nothing in this file mentions the camera.
 */

/* ── The view, as the engine publishes it ─────────────────────────────────── */

export type MahjongPhase = 'draw' | 'act' | 'claim';
export type MahjongMeldKind = 'chow' | 'pong' | 'kang';

export interface MahjongMeld {
  id: number;
  owner: number;
  kind: MahjongMeldKind;
  tiles: string[];
  /** Four in hand, declared. Announced to the table, but claimed from nobody. */
  concealed: boolean;
  from: number;
}

export interface MahjongDiscard {
  tile: string;
  by: number;
}

/** One scoring combo that fired, already multiplied by `count`. */
export interface AmbisyonCombo {
  key: string;
  en: string;
  tl: string;
  pts: number;
  count: number;
}

export interface MahjongOverView {
  reason: string;
  winner: number | null;
  /** Seat whose discard completed the hand, or null on a self-draw. */
  fedBy: number | null;
  winTile: string | null;
  /** The winning rack, face up. Public the moment the hand ends; [] otherwise. */
  hand: string[];
  structure: string | null;
  combos: AmbisyonCombo[];
  total: number;
  quitter: number | null;
}

/** The engine's own action shapes. Sent back verbatim from the legal list. */
export type MahjongAction =
  | { kind: 'draw' }
  | { kind: 'discard'; tile: string }
  | { kind: 'chow'; tiles: string[] }
  | { kind: 'pong'; tile?: string }
  | { kind: 'kang'; how: 'exposed' | 'concealed' | 'promoted'; tile?: string }
  | { kind: 'win' }
  | { kind: 'pass' };

export interface MahjongView {
  /** Seat index, or null on the rail. */
  you: number | null;
  hand: string[];
  /** The tile you just took. It is already inside `hand`; this only names it. */
  drawn: string | null;
  /** Public: how many tiles each seat conceals. Never which ones. */
  handCounts: number[];
  melds: MahjongMeld[];
  flowers: string[][];
  /** Oldest first. The open discard is its tail, which is what the board rings. */
  river: MahjongDiscard[];
  /** Public count of a private wall — its order is as secret as a rack. */
  wallCount: number;
  turn: number | null;
  phase: MahjongPhase;
  /** Open claim window: the tile, who threw it, and which seat is being polled. */
  claim: { tile: string; by: number; seat: number } | null;
  legal: MahjongAction[];
  over: MahjongOverView | null;
}

/** A miron arrives with this seat index — no seat, no taps, no "mine". */
const SPECTATOR_SEAT = -1;

/**
 * Seat winds. Turn order is East → South → West → North for seats 0→3, stated
 * in the engine's own types.ts; this only prints it. It decides nothing — the
 * chow-eligible seat comes from the engine's legal list, never from this table.
 */
const SEAT_WIND = ['E', 'S', 'W', 'N'] as const;

/**
 * Which edge of the felt a seat sits at once the room has rotated the local
 * player to the near one. GameRoom does exactly the same rotation for its pods
 * and stools, so a seat's meld pile and its pod always agree.
 */
const SEAT_AT = ['near', 'left', 'far', 'right'] as const;

/** Tiles in the far wall row, and the standing tiles down each side rail. */
const FAR_WALL = 14;
const SIDE_WALL = Array.from({ length: 8 }, (_, i) => (i - 3.5) * 24);

export function MahjongBoard(): JSX.Element {
  const match = useApp((s) => s.match);
  const t = useT();
  // The engine ships every ambisyon combo in both languages, so the banner
  // picks a field rather than looking one up.
  const lang = useLang();
  const [sel, setSel] = useState<string | null>(null);
  const [numerals, setNumerals] = useState(readNumerals);
  const poolRef = useRef<HTMLDivElement>(null);

  const view = useMemo(() => readMahjongView(match?.view), [match?.view]);

  // The server owns the deadline; this only renders the gap. Called
  // unconditionally so the claim clock survives a re-render.
  const clock = useTurnProgress(match?.deadline ?? null, match?.turnMs ?? 25_000);

  // Any new frame moved the rack under the selection.
  useEffect(() => {
    setSel(null);
  }, [match?.seq, match?.matchId]);

  /**
   * The pool reads oldest-first, so the tile that just landed is at the END of
   * a grid that only shows four rows of felt — and that tile is the one a
   * player must never have to hunt for.
   */
  const riverLen = view?.river.length ?? 0;
  useEffect(() => {
    const el = poolRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [riverLen]);

  if (!match || !view) {
    return <GameRoom felt={<p className="room__wait">{t('room.waiting.server')}</p>} />;
  }

  const {
    hand,
    drawn,
    melds,
    flowers,
    handCounts,
    river,
    wallCount,
    turn,
    phase,
    claim,
    legal,
    over,
  } = view;
  const mySeat = match.seat > SPECTATOR_SEAT ? match.seat : null;
  const watching = mySeat === null;
  const live = over === null;
  const myTurn = mySeat !== null && turn === mySeat && live;
  /** The interrupt: this seat is the one being polled on the open discard. */
  const calling = myTurn && phase === 'claim' && claim !== null;

  /* Affordances, all of them straight off the legal list. */
  const discardable = new Set(legal.flatMap((a) => (a.kind === 'discard' ? [a.tile] : [])));
  const canDraw = legal.some((a) => a.kind === 'draw');
  const canWin = legal.some((a) => a.kind === 'win');
  const canPass = legal.some((a) => a.kind === 'pass');
  const claimKang = legal.find((a) => a.kind === 'kang' && a.how === 'exposed') ?? null;
  const claimPong = legal.find((a) => a.kind === 'pong') ?? null;
  const chows = legal.flatMap((a) => (a.kind === 'chow' ? [a] : []));
  /** Kangs you declare out of your own rack — never part of a claim window. */
  const selfKangs = legal.flatMap((a) =>
    a.kind === 'kang' && a.how !== 'exposed' && a.tile !== undefined ? [{ ...a, tile: a.tile }] : [],
  );

  /**
   * The rack, and the tile you just took shown apart from it — the way a hand is
   * actually held. The engine sorts the drawn tile INTO the rack (it has to; a
   * tile id is not an identity and four copies of dots-5 are interchangeable),
   * so this lifts one instance back out. The multiset is unchanged.
   */
  const rack = [...hand];
  let drawnAt = -1;
  if (drawn !== null) {
    drawnAt = rack.indexOf(drawn);
    if (drawnAt >= 0) rack.splice(drawnAt, 1);
  }
  const fresh = drawnAt >= 0 ? drawn : null;

  const send = (action: MahjongAction): void => {
    socket.send({ t: 'match.action', matchId: match.matchId, seq: match.seq, action });
    setSel(null);
  };

  const tap = (tile: string): void => {
    if (!myTurn || phase !== 'act' || !discardable.has(tile)) return;
    setSel((cur) => (cur === tile ? null : tile));
  };

  const todas = over !== null && over.reason === 'win';
  const seconds = Math.ceil(clock.remainingMs / 1000);

  /*
   * The table. Only the pool and the melds lie on it; everything that stands on
   * a real table stands here too, and whose turn it is is the pods' job.
   */
  const felt = (
    <div className="mj" data-dry={wallCount === 0}>
      {/* The side rails of the wall — dressing. The wall's one published fact is
          how many tiles are left, and that is on the far row below. */}
      {SIDE_WALL.flatMap((dy) =>
        (['left', 'right'] as const).map((side) => (
          <span
            key={`${side}${dy}`}
            className="mj__wt up"
            data-side={side}
            style={{ '--dy': `${dy}px` } as CSSProperties}
            aria-hidden="true"
          />
        )),
      )}

      {/*
       * The far wall doubles as the bunot target, the way you would reach for
       * the wall itself. Its ORDER is as private as a rack, so there is nothing
       * here to peek at — the count above it is the only fact the view states.
       */}
      <button
        className="mj__bunot up"
        type="button"
        data-fly="stock"
        disabled={!canDraw}
        onClick={() => send({ kind: 'draw' })}
        aria-label={t(canDraw ? 'mj.wall.aria.draw' : 'mj.wall.aria', { n: wallCount })}
      >
        <span className="mj__walltag">
          {t('mj.wall')} <b className="data">{wallCount}</b>
        </span>
        <span className="mj__wtrow" aria-hidden="true">
          {Array.from({ length: FAR_WALL }, (_, i) => (
            <i key={i} />
          ))}
        </span>
      </button>

      {handCounts.map((count, seat) => (
        <div
          key={seat}
          className="mj__seat"
          data-at={SEAT_AT[rotate(seat, mySeat, handCounts.length)] ?? 'far'}
          data-fly={`melds-${seat}`}
        >
          {/* A concealed rack is a wall of backs with a number on it, never a
              face — and it stands, because that is how it sits on the table. */}
          {seat === mySeat ? null : (
            <span
              className="mj__hold up"
              data-fly={`hold-${seat}`}
              role="img"
              aria-label={t('mj.hold.aria', {
                who: seatName(t, seat, mySeat, watching),
                n: count,
              })}
            >
              <b className="data">{count}</b>
              <em>{SEAT_WIND[seat] ?? ''}</em>
            </span>
          )}
          <MeldStrip
            melds={melds.filter((m) => m.owner === seat)}
            flowers={flowers[seat] ?? []}
            size={seat === mySeat ? 'mini' : 'tiny'}
            numerals={numerals}
          />
        </div>
      ))}

      {/* The pool. Oldest first; the tile that just landed is ringed. */}
      <div
        className="mj__pool"
        ref={poolRef}
        data-fly="pile"
        role="group"
        aria-label={t('mj.pool.aria', { n: river.length })}
        data-tight={poolScale(river.length) < POOL_TIGHT}
        style={{ '--pool-k': poolScale(river.length) } as CSSProperties}
      >
        {river.map((d, i) => (
          <Tile
            key={`${d.tile}-${i}`}
            id={d.tile}
            size="mini"
            numerals={numerals}
            hot={i === river.length - 1}
          />
        ))}
      </div>
    </div>
  );

  /*
   * Your rack, on the near rail. Sixteen tiles plus the one you just took,
   * standing apart on the right — tap to pick, then Tapon in the pills. A single
   * tap that discarded outright is one mis-swipe away from throwing your pair.
   */
  const rail = watching ? undefined : (
    <div className="mj__rack" data-live={myTurn && phase === 'act'} data-fly="rack">
      {rack.length === 0 && fresh === null ? (
        <p className="mj__bare">{t('mj.rack.bare')}</p>
      ) : (
        <>
          {rack.map((id, i) => (
            <button
              key={`${id}-${i}`}
              className="mj__slot"
              type="button"
              data-picked={sel === id}
              data-usable={discardable.has(id)}
              disabled={!myTurn || phase !== 'act' || !discardable.has(id)}
              onClick={() => tap(id)}
              aria-pressed={sel === id}
              aria-label={tileName(t, id)}
            >
              <Tile id={id} numerals={numerals} />
            </button>
          ))}
          {fresh === null ? null : (
            <button
              className="mj__slot"
              type="button"
              data-fresh="true"
              data-picked={sel === fresh}
              data-usable={discardable.has(fresh)}
              disabled={!myTurn || phase !== 'act' || !discardable.has(fresh)}
              onClick={() => tap(fresh)}
              aria-pressed={sel === fresh}
              aria-label={t('mj.rack.fresh.aria', { tile: tileName(t, fresh) })}
            >
              <Tile id={fresh} numerals={numerals} />
            </button>
          )}
        </>
      )}
    </div>
  );

  /*
   * A claim window is an interrupt with a real clock, and it lands where every
   * other verb in the app lands: the action pills, bottom right. It cannot go
   * over the felt (that would cover the discard under claim) and it cannot go
   * over the rail (that is the sixteen tiles the decision is made against), so
   * the pills grow a head — the tile, who threw it, and the server's countdown.
   *
   * One poll offers ONE tier: a win, or the pong/kang pair, or up to three
   * chows — the engine's claim queue never mixes them (see buildClaimQueue).
   * The code still tolerates all three.
   */
  const actions =
    calling && claim !== null ? (
      <>
        <div className="mj__call">
          <Tile id={claim.tile} size="mini" numerals={numerals} hot />
          <span className="mj__callwho">
            <b>{seatName(t, claim.by, mySeat, watching)}</b>
            <i>{tileName(t, claim.tile)}</i>
          </span>
          <b className="mj__secs data" data-urgent={seconds <= 5}>
            {seconds}s
          </b>
          <div
            className="mj__callbar"
            role="progressbar"
            aria-label={t('mj.claim.time.aria')}
            aria-valuemin={0}
            aria-valuemax={Math.round((match.turnMs || 25_000) / 1000)}
            aria-valuenow={seconds}
            aria-valuetext={`${seconds}s`}
          >
            <i style={{ inlineSize: `${clock.elapsed01 * 100}%` }} />
          </div>
        </div>

        {canWin ? (
          <button className="room-act" data-tone="primary" type="button" onClick={() => send({ kind: 'win' })}>
            {t('mj.todas')}
          </button>
        ) : null}
        {claimKang ? (
          <button className="room-act" type="button" onClick={() => send({ kind: 'kang', how: 'exposed' })}>
            {t('mj.act.kang')}
          </button>
        ) : null}
        {claimPong ? (
          <button className="room-act" type="button" onClick={() => send({ kind: 'pong' })}>
            {t('mj.act.pong')}
          </button>
        ) : null}
        {chows.map((a) => (
          <button
            key={a.tiles.join('|')}
            className="room-act"
            type="button"
            onClick={() => send({ kind: 'chow', tiles: a.tiles })}
            aria-label={t('mj.act.chow.aria', {
              tiles: a.tiles.map((id) => tileName(t, id)).join(', '),
            })}
          >
            {/* The suit is named in the head above; the runs only need values. */}
            {t('mj.act.chow', { run: a.tiles.map((id) => valueOf(id)).join('-') })}
          </button>
        ))}
        {/* Always there, always last: a claim is an option, not a duty. */}
        <button
          className="room-act"
          type="button"
          disabled={!canPass}
          onClick={() => send({ kind: 'pass' })}
        >
          {t('mj.act.pass')}
        </button>
      </>
    ) : myTurn && phase !== 'claim' ? (
      <>
        {canWin ? (
          <button className="room-act" data-tone="primary" type="button" onClick={() => send({ kind: 'win' })}>
            {t('mj.todas')}
          </button>
        ) : null}
        {selfKangs.map((a) => (
          <button
            key={`${a.how}-${a.tile}`}
            className="room-act"
            type="button"
            onClick={() => send({ kind: 'kang', how: a.how, tile: a.tile })}
            aria-label={t('mj.act.kang.aria', { tile: tileName(t, a.tile) })}
          >
            {t('mj.act.kang.tile', { idx: cornerIndex(a.tile) })}
          </button>
        ))}
        {canDraw ? (
          <button className="room-act" data-tone="primary" type="button" onClick={() => send({ kind: 'draw' })}>
            {t('mj.act.draw')}
          </button>
        ) : null}
        {phase === 'act' ? (
          <button
            className="room-act"
            data-tone={canWin ? undefined : 'primary'}
            type="button"
            disabled={sel === null}
            onClick={() => sel !== null && send({ kind: 'discard', tile: sel })}
          >
            {t(sel === null ? 'mj.act.pick' : 'mj.act.discard')}
          </button>
        ) : null}
      </>
    ) : null;

  return (
    <GameRoom
      felt={felt}
      hand={rail}
      actions={actions}
      toast={
        calling && claim !== null ? (
          withMark(
            t('mj.toast.claim', { who: seatName(t, claim.by, mySeat, watching) }),
            '{tile}',
            tileName(t, claim.tile),
          )
        ) : (
          turnLine(t, view, myTurn, mySeat, watching)
        )
      }
      aside={
        <>
          {/*
           * §G1's accessibility toggle: Arabic-numeral corner indices. It is a
           * per-device rendering choice, so it lives in localStorage next to the
           * other prefs rather than on the account.
           *
           * Inert once the hand is over. The Todas slam used to absorb every tap
           * over this corner; now that the slam correctly lets taps through to
           * "pindutin para sa resibo", a tap aimed at dismissing the win would
           * land on the toggle instead and flip a saved preference.
           */}
          <button
            className={over === null ? 'mj__num' : 'mj__num room__inert'}
            type="button"
            disabled={over !== null}
            aria-pressed={numerals}
            aria-label={t('mj.numerals.aria')}
            onClick={() => {
              const next = !numerals;
              setNumerals(next);
              saveNumerals(next);
            }}
          >
            <Icon name="tile" size={13} />
            {t('mj.numerals')}
            <em>{t(numerals ? 'mj.numerals.on' : 'mj.numerals.off')}</em>
          </button>

          {/*
           * The signature moment (§G1). The one overlay allowed to cover the
           * table, and only ever once `over` is set: the hand is finished, no
           * tap does anything, and there is nothing underneath left to read.
           */}
          {over !== null ? (
            <div className="mj__todas room__inert" role="status" data-quiet={!todas}>
              <div className="todas__flare" aria-hidden="true" />
              <div className="todas__push">
                {todas ? (
                  <>
                    <p className="todas__title">{t('mj.todas')}</p>
                    <p className="todas__sub">
                      {over.fedBy === null
                        ? t('mj.todas.self', {
                            who: seatName(t, over.winner ?? 0, mySeat, watching),
                          })
                        : t('mj.todas.fed', {
                            who: seatName(t, over.winner ?? 0, mySeat, watching),
                            from: seatName(t, over.fedBy, mySeat, watching),
                          })}
                      {over.structure === 'sietePares' ? <> {t('mj.todas.siete')}</> : null}
                    </p>

                    {/* The hand tips forward tile by tile. Delay is set per tile. */}
                    <div className="todas__hand">
                      {over.hand.map((id, i) => (
                        <span
                          key={`${id}-${i}`}
                          className="todas__tile"
                          style={{ animationDelay: `${60 + i * 44}ms` }}
                          data-win={id === over.winTile}
                        >
                          <Tile id={id} size="mini" numerals={numerals} />
                        </span>
                      ))}
                    </div>

                    {/* The ambisyon banner: every combo that fired, itemised. */}
                    <ul className="todas__ambisyon">
                      {over.combos.map((c, i) => (
                        <li key={c.key} style={{ animationDelay: `${240 + i * 70}ms` }}>
                          <span>{lang === 'en' ? c.en : c.tl}</span>
                          {c.count > 1 ? <em>×{c.count}</em> : null}
                          <b className="data">{c.pts}</b>
                        </li>
                      ))}
                      <li
                        className="todas__total"
                        style={{ animationDelay: `${300 + over.combos.length * 70}ms` }}
                      >
                        <span>{t('mj.todas.total')}</span>
                        <b className="data">{over.total}</b>
                      </li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p className="todas__title" data-quiet="true">
                      {t(over.reason === 'washout' ? 'mj.over.washout' : 'mj.over.title')}
                    </p>
                    <p className="todas__sub">
                      {over.reason === 'washout'
                        ? t('mj.over.washout.sub')
                        : over.quitter !== null
                          ? t('mj.over.quit', {
                              who: seatName(t, over.quitter, mySeat, watching),
                            })
                          : t('mj.over.none')}
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </>
      }
    />
  );
}

/* ── Melds and the flower shelf ───────────────────────────────────────────── */

/**
 * One seat's exposed melds plus its flower shelf, lying flat in front of that
 * seat. Both are public the moment they land — the table watched every one of
 * them — so this renders faces, and a declared concealed kang is drawn face up
 * under a dashed rule, because it was announced even though nobody claimed it.
 *
 * A `tiny` strip belongs to a seat across the table and gets about a hundred
 * pixels of felt for up to five melds, so a pong and a kang collapse to one tile
 * and a count. Nothing is lost — they are three or four copies of the same tile
 * — and it is the difference between five melds fitting beside the pool and
 * spilling over it.
 */
function MeldStrip({
  melds,
  flowers,
  size,
  numerals,
}: {
  melds: MahjongMeld[];
  flowers: string[];
  size: 'tiny' | 'mini';
  numerals: boolean;
}): JSX.Element | null {
  const t = useT();
  if (melds.length === 0 && flowers.length === 0) return null;
  return (
    <span className="mj__melds">
      {melds.map((m) => {
        const head = m.tiles[0];
        const stacked = size === 'tiny' && m.kind !== 'chow' && head !== undefined;
        return (
          <span
            className="mj__meld"
            key={m.id}
            role="img"
            data-kind={m.kind}
            data-concealed={m.concealed}
            aria-label={meldLabel(t, m)}
          >
            {stacked && head !== undefined ? (
              <>
                <Tile id={head} size={size} numerals={numerals} />
                <em>×{m.tiles.length}</em>
              </>
            ) : (
              m.tiles.map((id, i) => (
                <Tile key={`${id}-${i}`} id={id} size={size} numerals={numerals} />
              ))
            )}
          </span>
        );
      })}
      {flowers.length === 0 ? null : (
        <span
          className="mj__shelf"
          role="img"
          aria-label={t('mj.shelf.aria', { n: flowers.length })}
        >
          {flowers.map((id, i) => (
            <Tile key={`${id}-${i}`} id={id} size={size} numerals={numerals} />
          ))}
        </span>
      )}
    </span>
  );
}

/* ── The tile ─────────────────────────────────────────────────────────────── */

/**
 * Ivory face (component sheet · §1c). The art is the doc's, pip for pip: five
 * balls are a quincunx and nine bamboo are a red cross through a green field,
 * which an auto-flowing grid gets wrong while still counting correctly.
 *
 * Two faces, and the split is the doc's own. A 36×50 rack tile has room to be
 * drawn; a 26×36 discard tile does not, and gets the doc's two-line
 * abbreviation instead — a value over a suit mark. Nothing tries to fit nine
 * countable balls into 26px.
 *
 * The optional corner index is §G1's accessibility toggle: value plus a suit
 * initial from the engine's own Tagalog names — B for Bilog, K for Kawayan, C
 * for Caracteres — and a single letter for an honor.
 */
export function Tile({
  id,
  size = 'full',
  numerals = false,
  hot = false,
}: {
  id: string;
  size?: TileSize;
  numerals?: boolean;
  hot?: boolean;
}): JSX.Element {
  const suit = suitOf(id);
  const value = valueOf(id);
  const glyphs = useTileGlyphs();
  return (
    <span
      className="mj-tile"
      data-suit={suit}
      data-size={size}
      data-hot={hot}
      data-art={suit === 'dots' || suit === 'bamboo'}
      aria-hidden="true"
    >
      <span className="mj-tile__face">
        <TileFace suit={suit} value={value} size={size} glyphs={glyphs} />
      </span>
      {/*
        The rank, in the far corner from the toggle's index so the two never
        argue over the same pixels.

        It rides the drawn face exactly: a drawn tile states its number only by
        being countable, and counting nine bamboo mid-hand is a chore the tile
        can do for you. `tiny` keeps the abbreviation, which is already a
        numeral, so a badge there would be the same digit printed twice.
      */}
      {size !== 'tiny' && RANKED.has(suit) ? (
        <span className="mj-tile__rank">{value}</span>
      ) : null}
      {/* A tiny tile has no room for an index and never carries one. */}
      {numerals && size !== 'tiny' ? <span className="mj-tile__idx">{cornerIndex(id)}</span> : null}
    </span>
  );
}

/* ── The pool's own scale ─────────────────────────────────────────────────── */

/*
 * The discard pool's box, in design px. These mirror `.mj__pool` in
 * MahjongBoard.css and are copied rather than read because the room is a fixed
 * 880×495 design box — the pool is the same 176×164 at every viewport, so its
 * capacity is arithmetic rather than something to measure. `POOL_H` is the
 * block-size less the 8px gutter the open discard's caret needs above it.
 */
const POOL_W = 176;
const POOL_H = 156;
const POOL_TILE_W = 26;
const POOL_TILE_H = 36;
const POOL_GAP = 4;
/** Below this the corner rank would be under 6px, which is dirt, not a number. */
export const POOL_TIGHT = 0.8;

/**
 * How far to shrink the pool so every discard is on the table.
 *
 * A 144-tile set leaves up to ~80 tiles in the river, and at full size the box
 * holds 24. It used to scroll — which meant that from the twenty-fifth discard
 * on, the tiles a player most needs to count were simply above the fold, behind
 * a gesture nobody makes mid-hand. Shrinking is the honest trade: a small tile
 * you can see beats a big one you cannot.
 *
 * Solved by column count rather than by stepping a scale down, because the
 * columns are what the width actually quantises to: for `cols` columns the
 * largest tile that fits across is exact, and the first column count whose rows
 * also fit down the box wins. The half-pixel is slack for `auto-fill`, which
 * decides the real column count from the same width — without it a track
 * landing on 10.997 columns rounds to 10 and the last row drops out.
 */
export function poolScale(n: number): number {
  for (let cols = 6; cols <= 20; cols++) {
    const across = POOL_TILE_W * cols + POOL_GAP * (cols - 1);
    // Six columns of 26px is the design and comes to 176px on the nose, so it
    // needs no slack; every scale below it is derived and gets the half-pixel.
    const k = across <= POOL_W ? 1 : (POOL_W - 0.5) / across;
    const rows = Math.ceil(n / cols);
    if (rows * POOL_TILE_H * k + (rows - 1) * POOL_GAP * k <= POOL_H) return k;
  }
  return (POOL_W - 0.5) / (POOL_TILE_W * 20 + POOL_GAP * 19);
}

/* ── Tile ids ─────────────────────────────────────────────────────────────── */

/**
 * Ids are `"<suit>-<value>"` (packages/engines/src/mahjong/tiles.ts). Splitting
 * one is rendering, not a rule — every legality question on this board is
 * answered by `view.legal`, and none of it is asked here.
 */
const suitOf = (id: string): string => id.slice(0, id.indexOf('-'));
const valueOf = (id: string): string => id.slice(id.indexOf('-') + 1);

/** The suits that carry a value. A wind and a dragon are named whole, below. */
const SUIT_KEY: Record<string, CopyKey> = {
  dots: 'mj.tile.dots',
  bamboo: 'mj.tile.bamboo',
  chars: 'mj.tile.chars',
  flower: 'mj.tile.flower',
};
const SUIT_INITIAL: Record<string, string> = { dots: 'B', bamboo: 'K', chars: 'C' };
/** The three suits that run 1–9. A wind has no rank and a flower is not one. */
const RANKED = new Set(['dots', 'bamboo', 'chars']);
const WIND_KEY: Record<string, CopyKey> = {
  E: 'mj.tile.wind.E',
  S: 'mj.tile.wind.S',
  W: 'mj.tile.wind.W',
  N: 'mj.tile.wind.N',
};
const DRAGON_KEY: Record<string, CopyKey> = {
  red: 'mj.tile.dragon.red',
  green: 'mj.tile.dragon.green',
  white: 'mj.tile.dragon.white',
};
const KIND_KEY: Record<MahjongMeldKind, CopyKey> = {
  chow: 'mj.kind.chow',
  pong: 'mj.kind.pong',
  kang: 'mj.kind.kang',
};
/** R·G·P are distinct from the winds' E·S·W·N, so no index is ambiguous. */
const DRAGON_INITIAL: Record<string, string> = { red: 'R', green: 'G', white: 'P' };

function cornerIndex(id: string): string {
  const suit = suitOf(id);
  const value = valueOf(id);
  if (suit === 'wind') return value;
  if (suit === 'dragon') return DRAGON_INITIAL[value] ?? '?';
  if (suit === 'flower') return `✿${value}`;
  return `${value}${SUIT_INITIAL[suit] ?? ''}`;
}

function tileName(t: T, id: string): string {
  const suit = suitOf(id);
  const v = valueOf(id);
  const whole = suit === 'wind' ? WIND_KEY[v] : suit === 'dragon' ? DRAGON_KEY[v] : undefined;
  if (whole !== undefined) return t(whole);
  const key = SUIT_KEY[suit];
  // A suit this build has never heard of: say the engine's own id rather than
  // nothing. Not copy, so it is not in the dictionary.
  return key === undefined ? `${suit} ${v}` : t(key, { v });
}

function meldLabel(t: T, m: MahjongMeld): string {
  const head = m.tiles[0];
  if (head === undefined) return t(KIND_KEY[m.kind]);
  return t(m.concealed ? 'mj.meld.aria.closed' : 'mj.meld.aria', {
    kind: t(KIND_KEY[m.kind]),
    tile: tileName(t, head),
  });
}

/* ── Copy ─────────────────────────────────────────────────────────────────── */

function seatName(t: T, seat: number, mySeat: number | null, watching: boolean): string {
  if (!watching && seat === mySeat) return t('room.you');
  return t('room.seat', { n: seat + 1 });
}

/**
 * One placeholder `t` was not given, rendered as an element instead. The tile
 * under claim is the fact the line turns on and carries the toast's gold weight,
 * and word order puts it in a different place in each language.
 */
function withMark(line: string, token: string, mark: string): JSX.Element {
  const [head = '', tail = ''] = line.split(token);
  return (
    <>
      {head}
      <b>{mark}</b>
      {tail}
    </>
  );
}

/** Distance round the table from your left, so seats read in turn order. */
function rotate(seat: number, mySeat: number | null, seats: number): number {
  if (mySeat === null || seats <= 0) return seat;
  return (seat - mySeat + seats) % seats;
}

/**
 * One line. The room's pods already carry whose turn it is and the clock, so the
 * toast's job is the part the ring cannot say: which of the three phases the
 * table is in, and what this seat owes.
 */
function turnLine(
  t: T,
  view: MahjongView,
  myTurn: boolean,
  mySeat: number | null,
  watching: boolean,
): string {
  if (view.over !== null) return t('mj.turn.over');
  if (view.turn === null) return t('mj.turn.wait');
  if (view.phase === 'claim') {
    if (myTurn) return t('mj.turn.you.claim');
    return t('mj.turn.claim', { who: seatName(t, view.turn, mySeat, watching) });
  }
  if (!myTurn) return t('mj.turn.other', { who: seatName(t, view.turn, mySeat, watching) });
  if (view.phase === 'draw') return t('mj.turn.you.draw');
  return t('mj.turn.you.act');
}

/* ── Device preference ────────────────────────────────────────────────────── */

/**
 * The numeral-index toggle is per install, like motion and colour (§7.3), not
 * account state. It gets its own key rather than a field in screens/lib/prefs.ts
 * because it is the only pref scoped to one venue; fold it in there if a second
 * board ever wants one.
 */
const NUMERALS_KEY = 'tpm.mahjong.numerals';

function readNumerals(): boolean {
  try {
    return localStorage.getItem(NUMERALS_KEY) === '1';
  } catch {
    return false;
  }
}

function saveNumerals(on: boolean): void {
  try {
    localStorage.setItem(NUMERALS_KEY, on ? '1' : '0');
  } catch {
    // Private browsing, or storage full. The toggle still works for this session.
  }
}

/* ── Reading the engine's view ────────────────────────────────────────────── */

/**
 * The view arrives as `unknown` (the wire type must not know the rules), so it
 * is narrowed rather than cast. A malformed payload renders the waiting state
 * instead of throwing inside a live match.
 *
 * `legal` is filtered, not trusted wholesale: anything this reader does not
 * recognise is dropped, so a newer server can add an action without this build
 * putting an unpressable button in the rail.
 */
export function readMahjongView(raw: unknown): MahjongView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  if (!Array.isArray(v['handCounts'])) return null;

  const river = Array.isArray(v['river'])
    ? v['river'].flatMap((d): MahjongDiscard[] => {
        if (typeof d !== 'object' || d === null) return [];
        const x = d as Record<string, unknown>;
        return isTile(x['tile']) && isNum(x['by']) ? [{ tile: x['tile'], by: x['by'] }] : [];
      })
    : [];

  const phase = v['phase'] === 'act' ? 'act' : v['phase'] === 'claim' ? 'claim' : 'draw';

  return {
    you: isNum(v['you']) ? v['you'] : null,
    hand: tileList(v['hand']),
    drawn: isTile(v['drawn']) ? v['drawn'] : null,
    handCounts: v['handCounts'].filter(isNum),
    melds: Array.isArray(v['melds'])
      ? v['melds'].map(readMeld).filter((m): m is MahjongMeld => m !== null)
      : [],
    flowers: Array.isArray(v['flowers']) ? v['flowers'].map(tileList) : [],
    river,
    wallCount: isNum(v['wallCount']) ? v['wallCount'] : 0,
    turn: isNum(v['turn']) ? v['turn'] : null,
    phase,
    claim: readClaim(v['claim']),
    legal: Array.isArray(v['legal'])
      ? v['legal'].map(readAction).filter((a): a is MahjongAction => a !== null)
      : [],
    over: readOver(v['over']),
  };
}

function readMeld(raw: unknown): MahjongMeld | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  if (!isNum(m['id']) || !isNum(m['owner'])) return null;
  const tiles = tileList(m['tiles']);
  if (tiles.length === 0) return null;
  const kind = m['kind'];
  if (kind !== 'chow' && kind !== 'pong' && kind !== 'kang') return null;
  return {
    id: m['id'],
    owner: m['owner'],
    kind,
    tiles,
    concealed: m['concealed'] === true,
    from: isNum(m['from']) ? m['from'] : m['owner'],
  };
}

function readClaim(raw: unknown): { tile: string; by: number; seat: number } | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  if (!isTile(c['tile']) || !isNum(c['by']) || !isNum(c['seat'])) return null;
  return { tile: c['tile'], by: c['by'], seat: c['seat'] };
}

function readAction(raw: unknown): MahjongAction | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const a = raw as Record<string, unknown>;
  switch (a['kind']) {
    case 'draw':
      return { kind: 'draw' };
    case 'discard':
      return isTile(a['tile']) ? { kind: 'discard', tile: a['tile'] } : null;
    case 'chow': {
      const tiles = tileList(a['tiles']);
      return tiles.length === 3 ? { kind: 'chow', tiles } : null;
    }
    case 'pong':
      return isTile(a['tile']) ? { kind: 'pong', tile: a['tile'] } : { kind: 'pong' };
    case 'kang': {
      const how = a['how'];
      if (how !== 'exposed' && how !== 'concealed' && how !== 'promoted') return null;
      return isTile(a['tile']) ? { kind: 'kang', how, tile: a['tile'] } : { kind: 'kang', how };
    }
    case 'win':
      return { kind: 'win' };
    case 'pass':
      return { kind: 'pass' };
    default:
      return null;
  }
}

function readOver(raw: unknown): MahjongOverView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const combos = Array.isArray(o['combos'])
    ? o['combos'].flatMap((c): AmbisyonCombo[] => {
        if (typeof c !== 'object' || c === null) return [];
        const x = c as Record<string, unknown>;
        if (typeof x['key'] !== 'string') return [];
        return [
          {
            key: x['key'],
            en: typeof x['en'] === 'string' ? x['en'] : x['key'],
            tl: typeof x['tl'] === 'string' ? x['tl'] : x['key'],
            pts: isNum(x['pts']) ? x['pts'] : 0,
            count: isNum(x['count']) ? x['count'] : 1,
          },
        ];
      })
    : [];
  return {
    reason: typeof o['reason'] === 'string' ? o['reason'] : 'win',
    winner: isNum(o['winner']) ? o['winner'] : null,
    fedBy: isNum(o['fedBy']) ? o['fedBy'] : null,
    winTile: isTile(o['winTile']) ? o['winTile'] : null,
    hand: tileList(o['hand']),
    structure: typeof o['structure'] === 'string' ? o['structure'] : null,
    combos,
    total: isNum(o['total']) ? o['total'] : 0,
    quitter: isNum(o['quitter']) ? o['quitter'] : null,
  };
}

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
/** Shape only — `"<suit>-<value>"`. The engine owns the vocabulary. */
const isTile = (x: unknown): x is string => typeof x === 'string' && x.indexOf('-') > 0;
const tileList = (x: unknown): string[] => (Array.isArray(x) ? x.filter(isTile) : []);
